import { GoogleMap, InfoWindow, Marker, MarkerClusterer } from '@react-google-maps/api';
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { ReactComponent as AlertIcon } from '../../assets/icons/alert-icon.svg';
import AlertStationIcon from '../../assets/icons/dashboard/alert-station-locator.svg';
import { ReactComponent as RightArrow } from '../../assets/icons/dashboard/right-arrow.svg';
import { ReactComponent as UserIcon } from '../../assets/icons/dashboard/user-icon.svg';
import { ReactComponent as LocationIcon } from '../../assets/icons/location-icon.svg';
import { ReactComponent as SearchIcon } from '../../assets/icons/search.svg';
import { brandIcons, brandImage, metaValues } from '../../config/branding';
import { useGoogleMaps } from '../../contexts/google-map-context/GoogleMapContent';
import { useAppDispatch } from '../../redux/hooks';
import { setNotificationMessage } from '../../redux/slices/NotificationManagementSlice';
import ConvertUTCtoHHMM from '../../shared-functions/ConvertUTCtoHHMM';
import dashboardApi from '../../utils/api/dashboard';
import AutoCompleteSelectField from '../auto-complete-select-field/AutoCompleteSelectField';
import BatteryLoader from '../battery-loader/BatteryLoader';
import MyButton from '../button/Button';

interface StationProperties {
  id: string;
  position: { lat: number; lng: number };
  alert: boolean;
  name: string;
  address: string;
  isChargingPointAvailable?: boolean;
  isFaulted?: boolean;
  connectorStatuses?: any[];
}

/**
 * Validates the position of a station.
 * @param {any} position The position object.
 * @param {any} position.lat - The lat prop
 * @param {any} position.lng - The lng prop
 * @returns {any} The validated position or null if invalid.
 */
const validatePosition = (position: { lat: any; lng: any }) => {
  const lat = parseFloat(position.lat);
  const lng = parseFloat(position.lng);
  return !Number.isNaN(lat) && !Number.isNaN(lng) ? { lat, lng } : null;
};

/**
 * Generates an SVG station marker icon encoded as a data URI.
 * @param {string} color - The hex color code for the marker.
 * @returns {string} The data URI of the generated SVG icon.
 */
const getStationMarkerIcon = (color: string): string => {
  const svg = `
    <svg width="44" height="45" viewBox="0 0 44 45"
      fill="none" xmlns="http://www.w3.org/2000/svg">

      <path
        d="M22 2.91602C14.4186 2.91602 8.25 8.81563 8.25 16.0645C8.25 19.5166 9.82352 24.1073 12.9267 29.7096C15.4189 34.2076 18.3021 38.275 19.8017 40.2988C20.0551 40.6446 20.3864 40.9259 20.7688 41.1197C21.1512 41.3136 21.5739 41.4146 22.0026 41.4146C22.4313 41.4146 22.854 41.3136 23.2363 41.1197C23.6187 40.9259 23.95 40.6446 24.2034 40.2988C25.7005 38.275 28.5863 34.2076 31.0784 29.7096C34.1765 24.1091 35.75 19.5183 35.75 16.0645C35.75 8.81563 29.5814 2.91602 22 2.91602Z"
        fill="${color}"
      />

      <path
        fill-rule="evenodd"
        clip-rule="evenodd"
        d="M23.2551 19.1954H28.9444C29.3221 19.1944 29.6668 19.386 29.8276 19.6883C29.9884 19.9907 29.9367 20.3475 29.6944 20.6041L19.3279 31.6541C19.1341 31.8446 18.8213 31.9 18.5603 31.7892C18.2993 31.6784 18.1517 31.4285 18.2002 31.1757L20.3058 21.7309H15.0504C14.7553 21.7289 14.4766 21.6094 14.2927 21.4052C14.1088 21.202 14.0394 20.9356 14.1033 20.6809L16.9226 9.63769C17.0184 9.24687 17.4083 8.96785 17.8609 8.96591H25.2198C25.5259 8.96202 25.8145 9.08938 25.9951 9.30715C26.1823 9.51812 26.2428 9.79616 26.1591 10.0538L23.2551 19.1954Z"
        fill="white"
      />
    </svg>
  `;

  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
};
/**
 * Resolves the color of the station marker based on connector status.
 * Priority: Available → Charging → Reserved → Red
 * @param {StationProperties} marker - The station properties.
 * @returns {string} The hex color code.
 */
const getStationColor = (marker: StationProperties): string => {
  const statuses = marker.connectorStatuses || [];

  // 🟢 GREEN
  // If ANY connector is Available, show green.
  // Example: 20 connectors and even 1 is Available => Green.
  const hasAvailable = statuses.some(
    (item) => item.status === 'Available',
  );

  if (hasAvailable) {
    return '#22c55e';
  }

  // 🔵 BLUE
  // No Available connector.
  // If ANY connector is Charging / Preparing / Finishing => Blue.
  const hasCharging = statuses.some(
    (item) =>
      item.status === 'Charging' ||
      item.status === 'Preparing' ||
      item.status === 'Finishing',
  );

  if (hasCharging) {
    return '#3b82f6';
  }

  // 🟡 YELLOW
  // No Available and no Charging status.
  // If ANY connector is Reserved => Yellow.
  const hasReserved = statuses.some(
    (item) => item.status === 'Reserved',
  );

  if (hasReserved) {
    return '#FFC107';
  }

  // 🔴 RED
  // No Available, Charging, Preparing, Finishing, or Reserved.
  return '#e41010';
};

interface CustomDashboardMapProperties {
  filters?: {
    facilityIds: string[];
    stationIds: string[];
    deviceIds: string[];
    type: string[];
  };
  isWidget?: boolean;
  viewonly?: boolean;
  staticData?: any;
  showSearch?: boolean;
}

/**
 * Shared Map component for Dashboard and Custom Widgets
 * @param {any} param0 - The param0
 * @param {any} param0.filters - The filters prop
 * @param {any} param0.isWidget - The isWidget prop
 * @param {any} param0.viewonly - The viewonly prop
 * @param {any} param0.staticData - The staticData prop
 * @param {any} param0.showSearch - The showSearch prop
 */
function CustomDashboardMap({
  filters,
  isWidget = false,
  viewonly = false,
  staticData,
  showSearch = false,
}: CustomDashboardMapProperties) {
  const { t } = useTranslation('dashboard');
  const { isLoaded } = useGoogleMaps();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [selectedMarker, setSelectedMarker] = useState<StationProperties | null>(null);
  const [stationLocation, setStationLocation] = useState<StationProperties[]>([]);
  const [singleStationDetails, setSingleStationDetails] = useState<any>(null);
  const [searchStation, setSearchStation] = useState<string | number>('');
  const [center, setCenter] = useState({
    lat: metaValues.latLong.lat,
    lng: metaValues.latLong.lng,
  });

  /**
   * Helper to retrieve daily operating hours formatting for today.
   * @returns {string} The formatted operating hours label.
   */
  const getTodayHours = (): string => {
    if (!singleStationDetails) return '';

    let parsedHours: any = null;
    if (typeof singleStationDetails.operatingHours === 'string') {
      try {
        parsedHours = JSON.parse(singleStationDetails.operatingHours);
      } catch {
        parsedHours = null;
      }
    } else if (singleStationDetails.operatingHours) {
      parsedHours = singleStationDetails.operatingHours;
    }

    if (parsedHours) {
      const dayNames = [
        'sunday',
        'monday',
        'tuesday',
        'wednesday',
        'thursday',
        'friday',
        'saturday',
      ] as const;
      const today = dayNames[new Date().getDay()];
      const dayData = parsedHours[today];
      if (dayData) {
        if (dayData.status === 'closed') {
          return t('closedToday', 'Closed Today');
        }
        const openTime = dayData.open || dayData.from;
        const closeTime = dayData.close || dayData.to;
        if (openTime === '00:00' && closeTime === '23:59') {
          return t('open24Hours', 'Open 24 Hours');
        }
        if (openTime && closeTime) {
          return `${openTime} - ${closeTime}`;
        }
      }
    }

    // Fallback to opensAt and closesAt
    if (singleStationDetails.opensAt && singleStationDetails.closesAt) {
      return `${ConvertUTCtoHHMM(singleStationDetails.opensAt)} - ${ConvertUTCtoHHMM(
        singleStationDetails.closesAt,
      )}`;
    }

    return '--';
  };

  /**
   * Fetches the list of all charging stations based on filters.
   * @param {object} filterParameters - Filters for stations.
   */
  const fetchAllChargingStationList = (filterParameters: any = {}) => {
    const { facilityIds, stationIds } = filterParameters;
    dashboardApi
      .fetchAllChargingStationList({
        accountCustomId: facilityIds,
        stationIds,
      })
      .then((response) => {
        const stationList: StationProperties[] = response?.data?.map((station: any) => ({
          id: station._id,
          position: { lat: parseFloat(station.latitude), lng: parseFloat(station.longitude) },
          alert: station.isAlert,
          name: station.name,
          address: station.address,
          isChargingPointAvailable: station.isChargingPointAvailable,
          isFaulted: station.isFaulted,
          connectorStatuses: station.connectorStatuses,
        }));
        setStationLocation(stationList.filter((station) => station.address !== undefined));
      })
      .catch((error) => {
        dispatch(
          setNotificationMessage({
            message: error?.response?.data?.message,
            status: true,
            type: 'error',
            code: error?.response?.data?.code,
          }),
        );
      });
  };

  /**
   * Handles fetching details for a single station.
   * @param {any} marker - The station marker properties.
   */
  const handleFetchSingleStation = async (marker: StationProperties) => {
    const validPosition = validatePosition(marker.position);
    if (validPosition) {
      setCenter(validPosition);
      setSelectedMarker({ ...marker, position: validPosition });
      dashboardApi
        .getChargingStationDetails(marker.id)
        .then((response) => {
          if (response.data.length) {
            setSingleStationDetails(response.data?.[0]);
          }
        })
        .catch((error) => {
          dispatch(
            setNotificationMessage({
              message: error?.response?.data?.message,
              status: true,
              type: 'error',
              code: error?.response?.data?.code,
            }),
          );
        });
    }
  };

  /**
   * Filters stations by their details (name).
   * @param {string} name - Parameter name.
   * @param {any} value - The search value.
   */
  const handleFilterByStationDetails = (name: string, value: string | number) => {
    setSearchStation(value);
    const matchedStation = stationLocation.find((station) => station.name === value);
    if (matchedStation) {
      handleFetchSingleStation(matchedStation);
    }
  };

  useEffect(() => {
    if (viewonly || !isWidget) {
      fetchAllChargingStationList(filters || {});
    } else {
      // Customization mode
      const mockStations =
        staticData?.markers?.map((m: any) => ({
          id: m.id,
          position: { lat: parseFloat(m.lat), lng: parseFloat(m.lng) },
          alert: m.status === 'maintenance' || m.status === 'busy',
          name: m.name || 'Station',
          address: m.address || '',
          isChargingPointAvailable: m.isChargingPointAvailable,
          isFaulted: m.isFaulted,
          connectorStatuses: m.connectorStatuses || [],
        })) || [];
      setStationLocation(mockStations);
    }
  }, [filters, viewonly, isWidget, staticData]);

  if (!isLoaded) {
    return (
      <div
        style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <BatteryLoader />
      </div>
    );
  }

  const mapCenter = viewonly || !isWidget ? center : staticData?.center || center;
  const mapZoom = viewonly || !isWidget ? 7 : staticData?.zoom || 10;

  return (
    <div
      className={`custom-dashboard-map-container ${isWidget ? 'is-widget' : ''}`}
      style={{ width: '100%', height: '100%', position: 'relative' }}>
      {showSearch && (
        <div
          className="map-search-wrap"
          style={{ position: 'absolute', top: '10px', left: '130px', zIndex: 5, width: '250px' }}>
          <AutoCompleteSelectField
            inputLabel=""
            placeholder="Search"
            name="search"
            selectedValue={searchStation}
            onChangeValue={handleFilterByStationDetails}
            svgIcon={<SearchIcon />}
            options={stationLocation.map((station) => ({
              label: `${station.name} (${station.address})`,
              value: station.name,
            }))}
          />
        </div>
      )}

      <GoogleMap
        mapContainerStyle={{
          width: '100%',
          height: '100%',
          borderRadius: isWidget ? '8px' : '0px',
        }}
        center={mapCenter}
        zoom={mapZoom}>
        <MarkerClusterer>
          {(clusterer) => (
            <>
              {stationLocation.map((marker) => {
                const validPos = validatePosition(marker.position);
                return (
                  validPos && (
                    <Marker
                      key={marker.id}
                      position={validPos}
                      clusterer={clusterer}
                      icon={
                        marker.alert
                          ? {
                            url: AlertStationIcon,
                            scaledSize: new window.google.maps.Size(76, 47),
                          }
                          : {
                            url: getStationMarkerIcon(getStationColor(marker)),
                            scaledSize: new window.google.maps.Size(44, 45),
                          }
                      }
                      onClick={() => (viewonly || !isWidget) && handleFetchSingleStation(marker)}
                    />
                  )
                );
              })}
            </>
          )}
        </MarkerClusterer>

        {selectedMarker && (viewonly || !isWidget) && (
          <InfoWindow
            position={selectedMarker.position}
            onCloseClick={() => setSelectedMarker(null)}>
            {(() => {
              if (selectedMarker.alert) {
                return (
                  <div className="map-station-details-wrap alert-wrap">
                    <div className="map-station-details-container">
                      <span>
                        <AlertIcon />
                      </span>
                      <p>{t('stationPerformanceAlerts')}</p>
                    </div>
                  </div>
                );
              }
              if (singleStationDetails) {
                return (
                  <div className="map-station-active-container">
                    <div className="map-station-details">
                      <div className="details-header">
                        <div className="details-header-image-wrap">
                          <img
                            src={brandImage.chargingDevice}
                            alt=""
                            className="details-header-image"
                          />
                        </div>
                        <div className="name-and-status">
                          <h2>{singleStationDetails.name}</h2>
                          {singleStationDetails.isAvailable ? (
                            <div className="status-wrap active">
                              <div className="status-point" />
                              <p>{t('active')}</p>
                            </div>
                          ) : (
                            <div className="status-wrap in-active">
                              <div className="status-point in-active" />
                              <p>{t('inactive')}</p>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="address-details">
                        <div className="place-with-icon">
                          <span />
                          <span>
                            <LocationIcon />
                          </span>
                        </div>
                        <p>{singleStationDetails.address}</p>
                      </div>
                      <div className="user-details">
                        <span>
                          <UserIcon />
                        </span>
                        <span className="count">{singleStationDetails.clientsOnStation}</span>
                        <span className="message">{t('clientOnStation')}</span>
                      </div>
                    </div>
                    <div className="map-station-center-border" />
                    <div className="map-station-availability-wrap">
                      <div className="availability-status-wrap">
                        <div className="each-availability">
                          <span>
                            <brandIcons.dashboardClock />
                          </span>
                          <p>{getTodayHours()}</p>
                        </div>
                        <div className="each-availability">
                          <span>
                            <brandIcons.dashboardGas />
                          </span>
                          <p>
                            {singleStationDetails.totalChargingPoints} {t('devices')}
                          </p>
                        </div>
                        <div className="each-availability">
                          <span>
                            <brandIcons.dashboardCharge />
                          </span>
                          <p>
                            {singleStationDetails?.connectorsCount
                              ? singleStationDetails?.connectorsCount
                              : '0'}{' '}
                            {t('connector')}
                          </p>
                        </div>
                      </div>
                      <div className="view-more-button">
                        <MyButton
                          label="View more"
                          buttonType="submit"
                          disableHoverEffect
                          svgIcon={<RightArrow />}
                          onClickFunc={() => navigate('../operations/charging-station/approved')}
                        />
                      </div>
                    </div>
                  </div>
                );
              }
              return <BatteryLoader />;
            })()}
          </InfoWindow>
        )}
      </GoogleMap>
    </div>
  );
}

CustomDashboardMap.defaultProps = {
  filters: {
    facilityIds: [],
    stationIds: [],
    deviceIds: [],
    type: ['commercial'],
  },
  isWidget: false,
  viewonly: false,
  staticData: null,
  showSearch: false,
};

export default CustomDashboardMap;
